import { useEffect } from 'react';

// Process steps with images
const processSteps = [
  {
    id: 1,
    title: "Orientation gratuite",
    description: "Un conseiller analyse votre profil et définit avec vous un parcours personnalisé vers l'Allemagne.",
    image: "/images/process/orientation.png",
    color: "primary"
  },
  {
    id: 2,
    title: "Formation en Allemand",
    description: "Cours intensifs du niveau A1 à B2, en ligne ou en présentiel, avec des professeurs certifiés.",
    image: "/images/process/formation.png",
    color: "secondary"
  },
  {
    id: 3,
    title: "Préparation aux Examens",
    description: "Entraînement ciblé pour réussir les certifications Goethe-Institut, TELC et/ou ECL.",
    image: "/images/process/exams.png",
    color: "accent"
  },
  {
    id: 4,
    title: "Dossier Visa",
    description: "Accompagnement complet pour constituer un dossier solide. Garantie \"Votre succès, Notre priorité!\".",
    image: "/images/process/visa.png",
    color: "primary"
  },
  {
    id: 5,
    title: "Placement",
    description: "Accès à un réseau de 11+ partenaires en Allemagne (Université et Entreprise).",
    image: "/images/process/university.png",
    color: "secondary"
  },
  {
    id: 6,
    title: "Suivi post-arrivée",
    description: "Logement, banque, assurance, job étudiant : nous restons à vos côtés après votre arrivée.",
    image: "/images/process/support.png",
    color: "accent"
  }
];

// 3D Image component
const ProcessImage = ({ src, alt }) => {
  return (
    <div className="relative w-24 h-24 mx-auto">
      <img 
        src={src} 
        alt={alt}
        className="w-full h-full object-contain drop-shadow-lg"
        loading="lazy"
      />
    </div>
  );
};

// Simple animated illustration fallback using CSS
const AnimatedIllustration = ({ type, color }) => {
  const getIllustration = () => {
    switch(type) {
      case 1: // Orientation
        return (
          <div className="relative w-24 h-24 mx-auto">
            <div className={`absolute inset-0 bg-${color}/10 rounded-full animate-pulse`} />
            <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
              <svg className={`w-12 h-12 text-${color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className={`absolute -top-1 -right-1 w-4 h-4 bg-${color} rounded-full animate-bounce`} style={{ animationDelay: '0.2s' }} />
          </div>
        );
      case 2: // Formation
        return (
          <div className="relative w-24 h-24 mx-auto">
            <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-12 bg-${color}/20 rounded-t-lg`} />
            <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 w-12 h-10 bg-${color}/30 rounded-t-lg`} />
            <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 w-8 h-8 bg-${color} rounded-t-lg flex items-center justify-center animate-bounce`} style={{ animationDuration: '2s' }}>
              <span className="text-white text-xs font-bold">A→B</span>
            </div>
            <svg className={`absolute top-2 left-1/2 -translate-x-1/2 w-8 h-8 text-${color} animate-pulse`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
          </div>
        );
      case 3: // Exams
        return (
          <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
            <div className={`absolute inset-0 border-4 border-${color}/30 rounded-full animate-spin`} style={{ animationDuration: '8s' }} />
            <div className={`w-16 h-16 bg-${color}/10 rounded-xl flex items-center justify-center`}>
              <svg className={`w-10 h-10 text-${color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
              </svg>
            </div>
            <div className={`absolute top-0 right-2 w-3 h-3 bg-green-500 rounded-full animate-ping`} />
          </div>
        );
      case 4: // Visa
        return (
          <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
            <div className={`w-20 h-14 bg-${color}/10 rounded-lg border-2 border-${color}/30 flex items-center justify-center relative overflow-hidden`}>
              <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-${color}/20 to-transparent animate-shimmer`} />
              <svg className={`w-8 h-8 text-${color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className={`absolute -bottom-1 right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center animate-bounce`} style={{ animationDelay: '0.5s' }}>
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
        );
      case 5: // University
        return (
          <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
            <div className={`absolute bottom-2 w-20 h-10 bg-${color}/20 rounded`} />
            <div className={`absolute bottom-6 w-16 h-3 bg-${color}/40`} />
            <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[30px] border-r-[30px] border-b-[20px] border-transparent border-b-${color}/60`} />
            <div className={`absolute top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-${color} rounded-full animate-pulse`} />
            <svg className={`absolute top-6 left-1/2 -translate-x-1/2 w-6 h-6 text-${color}`} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 14l9-5-9-5-9 5 9 5z" />
              <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
            </svg>
          </div>
        );
      case 6: // Travel/Support
        return (
          <div className="relative w-24 h-24 mx-auto flex items-center justify-center">
            <div className={`w-14 h-16 bg-${color}/20 rounded-lg relative`}>
              <div className={`absolute -top-2 left-1/2 -translate-x-1/2 w-8 h-3 bg-${color}/40 rounded-t-lg`} />
              <div className={`absolute top-3 left-1/2 -translate-x-1/2 w-6 h-1 bg-${color}/60 rounded`} />
              <div className={`absolute top-5 left-1/2 -translate-x-1/2 w-6 h-1 bg-${color}/60 rounded`} />
            </div>
            <div className={`absolute -top-2 -right-2 animate-bounce`} style={{ animationDuration: '2s' }}>
              <svg className={`w-8 h-8 text-${color}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3l14 9-14 9V3z" />
              </svg>
            </div>
            <div className={`absolute bottom-0 left-0 w-3 h-3 bg-${color} rounded-full animate-ping`} style={{ animationDelay: '1s' }} />
          </div>
        );
      default:
        return null;
    }
  };

  return getIllustration();
};

const ProcessCard = ({ step, index }) => {
  const colorClasses = {
    primary: { bg: 'bg-primary', text: 'text-primary', light: 'bg-primary/10' },
    secondary: { bg: 'bg-secondary', text: 'text-secondary', light: 'bg-secondary/10' },
    accent: { bg: 'bg-accent', text: 'text-accent', light: 'bg-accent/10' }
  };

  const colors = colorClasses[step.color];

  return (
    <div 
      className="process-card opacity-0 translate-y-4 bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`process-card-${step.id}`}
    >
      {/* Image or CSS illustration */}
      <div className="mb-6 h-28 flex items-center justify-center">
        {step.image ? (
          <ProcessImage src={step.image} alt={step.title} />
        ) : (
          <AnimatedIllustration type={step.id} color={step.color} />
        )}
      </div>

      {/* Step number badge */}
      <div className={`inline-flex items-center gap-2 ${colors.light} ${colors.text} text-xs font-medium px-3 py-1 rounded-full mb-3`}>
        <span>Étape {step.id}</span>
      </div>

      {/* Title */}
      <h3 className="font-heading text-lg font-bold text-gray-900 mb-2">
        {step.title}
      </h3>

      {/* Description */}
      <p className="text-gray-500 text-sm leading-relaxed">
        {step.description}
      </p>
    </div>
  );
};

const ProcessSection = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const cards = entry.target.querySelectorAll('.process-card');
            cards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-in');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('parcours');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="parcours" className="py-16 md:py-24 bg-white" data-testid="process-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Votre parcours
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Notre méthode pour vous mener à la réussite ?
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Un parcours en 6 étapes, pensé pour vous guider du premier échange à votre arrivée en Allemagne.
          </p>
        </div>

        {/* Process Grid - 3x2 */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {processSteps.map((step, index) => (
            <ProcessCard key={step.id} step={step} index={index} />
          ))}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        .process-card.animate-in {
          opacity: 1;
          transform: translateY(0);
          transition: opacity 0.5s ease, transform 0.5s ease, box-shadow 0.3s ease;
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .text-primary { color: #cc007e; }
        .text-secondary { color: #004aad; }
        .text-accent { color: #f97316; }
        .bg-primary { background-color: #cc007e; }
        .bg-secondary { background-color: #004aad; }
        .bg-accent { background-color: #f97316; }
      `}</style>
    </section>
  );
};

export default ProcessSection;
